<script setup lang="ts">
import OffcanvasHelp from "@/components/layouts/navbar/OffcanvasHelp.vue";
import OffcanvasAccountInfo from "@/components/layouts/navbar/OffcanvasAccountInfo.vue";
import OffcanvasWhatsNew from "@/components/layouts/navbar/OffcanvasWhatsNew.vue";

import SettingsToggler from "@/components/layouts/navbar/SettingsToggler.vue";
import ReloadAppButton from "@/components/layouts/navbar/ReloadAppButton.vue";
import HeaderBreadcrumb from "@/components/layouts/breadcrumb/HeaderBreadcrumb.vue";
import DRLogo from "@/components/UI/DRLogo.vue";
import NavigationContent from "@/components/layouts/NavigationContent.vue";

import { useScreen } from "@/composable/useResponsive";
import {
  useEventsController,
  useGetMyLegends,
  useSyncLocalFiles,
} from "@/stores/queries";
import { HEADER_HEIGHT } from "@/style/constants";
import { useGetMe } from "@/stores/queries/auth.query";
import { computed, onMounted, onUnmounted, watch, ref } from "vue";
import { useDrawingStore } from "@/stores/fileStore";
import { useDeviceState } from "@/stores/DeviceState";
import { useStorageQuota } from "@/composable/useStorageQuota";
import { useMemoryWarning } from "@/composable/useMemoryWarning";
import { useToast } from "@/composable/useToast";

import { useTheme } from "@/composable/useTheme";
import { useLanguage } from "@/composable/useLanguage";
import OfflineBanner from "@/components/files/SyncList/OfflineBanner.vue";
import StorageBanner from "@/components/files/SyncList/StorageBanner.vue";
import MemoryBanner from "@/components/layouts/MemoryBanner.vue";
import BrandMigrationBanner from "@/components/brand-migration/BrandMigrationBanner.vue";
import { isLegacyBrand } from "@/brand/migration";
import ToastContainer from "@/components/UI/feedbacks/ToastContainer.vue";
import { useRoute } from "vue-router";
import { backupRepository, BACKUP_MAX_AGE_MS } from "@/repositories/backup.repository";
import { useLocalDrawingsRetention } from "@/composable/useLocalDrawingsRetention";
import { requestPersistentStorage } from "@/composable/usePWA";

useGetMyLegends();
useGetMe();
useSyncLocalFiles();
useStorageQuota();
useMemoryWarning();
useEventsController();
useTheme();
useLanguage();

// Politique de rétention : purge les backups locaux de plus de 2 mois,
// une fois par entrée dans l'app authentifiée (AuthLayout reste monté
// tant que l'utilisateur navigue dans l'app).
void backupRepository.pruneOlderThan(BACKUP_MAX_AGE_MS);

// Second volet de la même politique : retire du stockage hors ligne les dessins
// inactifs depuis 2 mois et sans travail en attente. Composable et non appel direct
// — il doit attendre que le liveQuery des dessins locaux soit chargé ET que
// l'appareil soit en ligne (cf. useLocalDrawingsRetention).
useLocalDrawingsRetention();

const { screen } = useScreen();

// Mobile menu state
const showMobileMenu = ref(false);
const isMobile = computed(() => !screen.value.gt.sm);

const openMobileMenu = () => {
  showMobileMenu.value = true;
};

const closeMobileMenu = () => {
  showMobileMenu.value = false;
};

// Close mobile menu on route change
const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    closeMobileMenu();
  }
);

const drawingStore = useDrawingStore();
const deviceState = useDeviceState();
const { showToast } = useToast();

onMounted(() => {
  deviceState.startMonitoringOnlineState();

  // Protège les plans hors ligne de l'éviction par le navigateur (cf. usePWA).
  void requestPersistentStorage();

  // Show a deferred toast set by DrawingCanvas when a disabled DrawingWeb version was detected.
  const pendingToast = sessionStorage.getItem("pendingToast");
  if (pendingToast) {
    sessionStorage.removeItem("pendingToast");
    showToast(pendingToast, "warning");
  }
});

watch(
  () => deviceState.isOnline,
  (isOnline) => {
    if (isOnline === null) return;
    if (isOnline) {
      drawingStore.startAutoSync();
    } else {
      drawingStore.stopAutoSync();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  deviceState.stopMonitoringOnlineState();
  drawingStore.stopAutoSync();
});
</script>

<template>
  <el-container class="h-100">
    <!-- Sidebar - hidden on mobile -->
    <el-aside
      v-if="!isMobile"
      :width="screen.lt.md ? '4rem' : '15rem'"
      class="h-100 border-end d-flex flex-column"
      style="background-color: var(--el-menu-bg-color)"
    >
      <!-- Logo centré dans la barre latérale, à la hauteur de l'en-tête de page
           pour que les deux bandeaux soient alignés. -->
      <div
        class="d-flex justify-content-center align-items-center"
        :style="{ height: `${HEADER_HEIGHT}px` }"
      >
        <DRLogo :compact="screen.lt.md" />
      </div>

      <div class="flex-grow-1 overflow-hidden">
        <NavigationContent :minimal="screen.lt.md" />
      </div>
    </el-aside>

    <el-container>
      <el-header
        class="ps-1 d-flex align-items-center gap-3 border-bottom"
        :style="isMobile ? { backgroundColor: 'var(--bs-body-bg)' } : {}"
        :height="`${HEADER_HEIGHT}px`"
      >
        <!-- Hamburger menu button - mobile only -->
        <button v-if="isMobile" class="hamburger-btn" @click="openMobileMenu">
          <i class="bi-list" />
        </button>

        <div class="ps-3 flex-grow-1">
          <HeaderBreadcrumb />
        </div>

        <div class="d-flex align-items-center gap-4">
          <ReloadAppButton />
          <SettingsToggler />
          <OffcanvasWhatsNew />
          <OffcanvasHelp />
          <OffcanvasAccountInfo />
        </div>
      </el-header>

      <BrandMigrationBanner v-if="isLegacyBrand" />
      <OfflineBanner />
      <StorageBanner />
      <MemoryBanner />

      <el-main style="padding: 0">
        <el-scrollbar>
          <RouterView></RouterView>
        </el-scrollbar>
      </el-main>
    </el-container>
  </el-container>

  <!-- Mobile menu overlay -->
  <Teleport to="body">
    <Transition name="mobile-menu">
      <div v-if="showMobileMenu" class="mobile-menu-overlay">
        <div class="mobile-menu-header">
          <DRLogo />
          <button class="close-btn" @click="closeMobileMenu">
            <i class="bi-x-lg" />
          </button>
        </div>

        <NavigationContent :minimal="false" class="flex-grow-1" />
      </div>
    </Transition>
  </Teleport>

  <ToastContainer />
</template>

<style lang="scss">
body {
  margin: 0;
  padding: 0;
  background: var(--bs-light);
}
</style>

<style lang="scss" scoped>
.hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: var(--bs-body-color);
  font-size: 24px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }
}

.mobile-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: var(--el-menu-bg-color);
  display: flex;
  flex-direction: column;
}

.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--bs-border-color);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
  font-size: 20px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }
}

// Transition
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: transform 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  transform: translateX(-100%);
}
</style>
